<template>
	<paginated-results
		v-slot="{
			doSearch,
			empty,
			hasNextPage,
			hasPrevPage,
			loading,
			nextPage,
			prevPage,
			page,
			pages,
			refresh,
			results,
			total,
		}"
		v-bind="{
			action,
			filters,
			search,
			perPage,
			sort,
			sortDescending,
		}"
	>
		<b-container fluid class="mt-4">
			<b-row>
				<b-col cols="12" md="12" lg="4" order="1" class="mb-4 mb-lg-0">
					<b-form @submit.prevent="doSearch">
						<search-input v-model="search" v-bind="{ loading }" />
					</b-form>
				</b-col>
				<b-col cols="6" md="6" lg="2" order="2" class="text-left text-md-left text-lg-left d-flex align-items-center">
						<!-- Filter Dropdown -->
						<b-dropdown split @click="filtering = !filtering" :pressed="filtering" class="mr-2">
							<template #button-content>
							<font-awesome-icon icon="filter" fixed-width />
							<span>Filter</span>
							<span v-if="activeFiltersCount > 0">({{ activeFiltersCount }})</span>
							</template>
							<b-dropdown-item-button @click="resetFilters" :disabled="loading" title="Clear all filters">
							<span>Clear Filters</span>
							</b-dropdown-item-button>
							<b-dropdown-item-button @click="refresh" :disabled="loading" title="Refresh">
							<span>Refresh</span>
							</b-dropdown-item-button>
						</b-dropdown>

						<!-- Badge -->
						<b-badge v-if="loading" class="text-bold p-2 m-0 d-flex align-items-center justify-content-center" pill  style="color:white; background-color:#f59e59; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
							Loading...
						</b-badge>
						<b-badge v-else-if="total > 0" class="text-bold p-2 m-0 d-flex align-items-center justify-content-center" pill style="color:white; background-color:#f59e59; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
							<span style="font-size: 1.5em;">{{ total.toLocaleString() }}</span>
						</b-badge>
						</b-col>
				<b-col cols="6" md="6" lg="6" order="3" class="text-right">
						<div  class="d-flex justify-content-end align-items-center">
						<p class="font-weight-bold small text-muted mb-0 mr-3">
							Page {{ page }} / {{ pages }}
						</p>
						<simple-pagination v-bind="{ loading, nextPage, prevPage, hasPrevPage, hasNextPage }" />
						</div>
				</b-col>
			</b-row>
		</b-container>

		<b-container fluid>
			<b-row>
				<b-col cols="12">
					<b-collapse v-model="filtering" class="py-2">
						<b-form @submit.prevent="doSearch">
							<b-card>
								<IndexFilters v-model="filters" :disabled="loading" hide-status />
							</b-card>
						</b-form>
					</b-collapse>
				</b-col>
			</b-row>
		</b-container>

		<b-container v-if="loading && empty" class="my-4">
			<loading-indicator size="4x" class="my-5" />
		</b-container>
		<b-container fluid v-else-if="!empty" class="mt-4">
			<b-row>
				<b-col cols="12" class="mb-4">
					<ResultTable
						:data="results"
						:loading="loading"
						:sort.sync="sort"
						:sort-descending.sync="sortDescending"
						@clicked="toView"
					/>
				</b-col>
			</b-row>
		</b-container>
		<b-container fluid v-else class="my-4">
			<empty-result>
				No UTC appeals found
				<template #content> No unable-to-complete appeals have been created or match your search. </template>
			</empty-result>
		</b-container>
	</paginated-results>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { getIndex } from "@/clients/services/appeals";

import ResultTable from "@/clients/components/Appeals/Table.vue";
import IndexFilters from "@/clients/components/Appeals/Filters.vue";

const defaultFilters = {
	denial_type_id: null,
	facility_id: null,
	visit_number: null,
	insurance_provider_id: null,
	insurance_type_id: null,
	insurance_plan: null,
	insurance_number: null,
	appeal_level_id: null,
	appeal_type_id: null,
	case_outcome_id: null,
	assigned_to: null,
	admit_date: null,
	discharge_date: null,
	defendable: null,
	priority: null,
	audit_reviewer_id: null,
	agency_id: null,
	id:null,
	case_status: null,
	division: null,
	chain_name: null,
	is_rebill:null,
};

const filters = reactive({ status: "UTC", ...defaultFilters });
const filtering = ref(false);
const resetFilters = () => Object.assign(filters, defaultFilters);

const router = useRouter();

const toView = function (entity) {
	router.push({
		name: "appeals.view",
		params: {
			id: entity.case_id,
			appeal_id: entity.id,
		},
	});
};

const action = getIndex;
// const search = ref("");
// Get initial value from sessionStorage if available
const search = ref(sessionStorage.getItem('appeal_search') || ""); 
const sort = ref("created");
const sortDescending = ref(true);
const perPage = ref(15);


const normalizedFilters = computed(() => {
    const { status, ...otherFilters } = filters;
    return {
        ...otherFilters,
        id: filters.id === '' ? null : filters.id,
		facility_id: filters.facility_id === '' ? null : filters.facility_id,
    };
});

const activeFiltersCount = computed(() => {
    return Object.values(normalizedFilters.value).filter(value => value !== null).length;
});

watch(
  () => search.value, // Watch search.value
  (newValue, oldValue) => {
	sessionStorage.setItem('appeal_search', newValue); // Store newValue in sessionStorage
  }
);
</script>
